<script lang="ts">
    import { createEventDispatcher } from "svelte";
    import CardBack from "./CardBack.svelte";

    export let id: number;
    export let rank: number;
    export let suit: number;
    export let faceUp: boolean = false;
    export let backVariant: number | string = 0;
    export let highContrastMode: boolean = false;
    export let isPickedUp: boolean = false;

    const dispatch = createEventDispatcher<{ activate: { cardId: number } }>();

    const rankSymbols = [
        "",
        "A",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "T",
        "J",
        "Q",
        "K",
    ] as const;
    const rankNames = [
        "",
        "Ace",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "10",
        "Jack",
        "Queen",
        "King",
    ] as const;
    const suitSymbols = ["S", "H", "D", "C"] as const;
    const suitNames = ["Spades", "Hearts", "Diamonds", "Clubs"] as const;
    const displaySuitSymbols = ["♠", "♥", "♦", "♣"];
    const suitColors = ["black", "#d40000", "#d40000", "black"];

    const getRankSymbol = (value: number) =>
        value > 0 && value < rankSymbols.length
            ? rankSymbols[value]
            : value.toString();
    const getDisplayRank = (value: number) => {
        if (value === 10) return "10";
        return getRankSymbol(value);
    };

    const getRankName = (value: number) =>
        value > 0 && value < rankNames.length
            ? rankNames[value]
            : value.toString();
    const getSuitSymbol = (value: number) =>
        value >= 0 && value < suitSymbols.length
            ? suitSymbols[value]
            : "unknown";
    const getSuitName = (value: number) =>
        value >= 0 && value < suitNames.length ? suitNames[value] : "unknown";

    $: fileRank = getRankSymbol(rank);
    $: fileSuit = getSuitSymbol(suit);
    $: cardImage = `${import.meta.env.BASE_URL}card_faces3/${fileRank}${fileSuit}.svg`;

    // Readable labels for screen readers
    $: rankName = getRankName(rank);
    $: suitName = getSuitName(suit);

    // Full aria label
    $: ariaLabel = faceUp ? `${rankName} of ${suitName}` : "Face down card";

    function activate() {
        dispatch("activate", { cardId: id });
    }

    function handleKeydown(e: KeyboardEvent) {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            activate();
        }
    }
</script>

<div
    class="scene"
    class:picked-up={isPickedUp}
    data-card-id={id}
    on:keydown={handleKeydown}
    on:contextmenu|preventDefault
    role="button"
    tabindex="0"
    aria-label={ariaLabel}
    draggable="false"
>
    <div class="card-inner" class:is-flipped={!faceUp}>
        <div class="card-face card-front">
            {#if highContrastMode}
                <div
                    class="high-contrast-face"
                    style="color: {suitColors[suit]}"
                >
                    <div class="hc-top">
                        <span class="hc-rank">{getDisplayRank(rank)}</span>
                        <span class="hc-suit-small"
                            >{displaySuitSymbols[suit]}</span
                        >
                    </div>
                    <div class="hc-bottom">
                        <span class="hc-suit-large"
                            >{displaySuitSymbols[suit]}</span
                        >
                    </div>
                </div>
            {:else}
                <img
                    src={cardImage}
                    alt=""
                    class="card-image"
                    draggable="false"
                />
            {/if}
        </div>
        <div class="card-face card-back">
            <CardBack variant={backVariant} />
        </div>
    </div>
</div>

<style>
    /* The Scene: Sets the distance of the viewer from the object */
    .scene {
        width: var(--base-card-width, 100px);
        height: var(--base-card-height, 140px);
        transform: scale(var(--card-scale, 1));
        transform-origin: top left;
        perspective: 1000px; /* Essential for the 3D effect */
        cursor: pointer;
        display: inline-block;
        user-select: none;
        -webkit-user-select: none;
        pointer-events: auto;
        -webkit-touch-callout: none;
        transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
    }

    .scene.picked-up {
        transform: translate(
                calc(
                    var(--base-card-width, 100px) * var(--card-scale, 1) * -0.05
                ),
                calc(
                    var(--base-card-height, 140px) * var(--card-scale, 1) *
                        -0.05
                )
            )
            scale(calc(var(--card-scale, 1) * 1.1));
        z-index: 100;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
    }

    /* The Object: The actual element that rotates */
    .card-inner {
        width: 100%;
        height: 100%;
        position: relative;
        transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1); /* Smooth ease-in-out */
        transform-style: preserve-3d;
        transform-origin: center; /* Ensures rotation about the center axis */
    }

    /* State: When flipped (face down), rotate the wrapper */
    .card-inner.is-flipped {
        transform: rotateY(180deg);
    }

    /* Faces: Common styles for both sides */
    .card-face {
        position: absolute;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        backface-visibility: hidden; /* Hides the back of the div when rotated */
        -webkit-backface-visibility: hidden; /* Safari support */
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px; /* Applied here to clip contents */
        overflow: hidden; /* Ensures image/bg doesn't bleed */
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(0, 0, 0, 0.3);
    }

    /* Front Face: Default state (0deg) */
    .card-front {
        transform: rotateY(0deg);
        background-color: rgb(206, 40, 40);
    }

    /* Back Face: Rotated 180deg so it faces away initially */
    .card-back {
        transform: rotateY(180deg);
        /* Background handled by CardBack component */
    }

    .card-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
        pointer-events: none;
    }

    .high-contrast-face {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background-color: white;
        font-weight: 900;
        padding: 2px 4px 4px 4px;
        box-sizing: border-box;
    }

    .hc-top {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        gap: 4px;
        line-height: 1;
        margin-top: 0;
    }

    .hc-bottom {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }

    .hc-rank {
        font-size: 52px;
        letter-spacing: -3px;
    }

    .hc-suit-small {
        font-size: 40px;
    }

    .hc-suit-large {
        font-size: 80px;
        line-height: 1;
    }
</style>
